<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import FavoriteListsAPI from 'dashboard/api/favoriteLists';
import LaisCrmContactsAPI from 'dashboard/api/laisCrmContacts';
import { useAlert, useTracking } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import CreateEditListDialog from '../components/CreateEditListDialog.vue';
import DeleteListDialog from '../components/DeleteListDialog.vue';

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { track } = useTracking();
const ACTIVE_LIST_WINDOW_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const routeString = value => (Array.isArray(value) ? value[0] : value);
const normalizedAvailabilityFilter = value =>
  ['all', 'available', 'unavailable'].includes(value) ? value : 'all';
const normalizedBusinessContextFilter = value =>
  ['all', 'aluguel', 'venda'].includes(value) ? value : 'all';
const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  );

const lists = ref([]);
const contact = ref(null);
const loading = ref(false);
const contactLoading = ref(false);
const creating = ref(false);
const deleting = ref(false);
const showCreateDialog = ref(false);
const showDeleteDialog = ref(false);
const listPendingDeletion = ref(null);
const listSearch = ref(routeString(route.query.q) || '');
const availabilityFilter = ref(
  normalizedAvailabilityFilter(routeString(route.query.disponibilidade))
);
const businessContextFilter = ref(
  normalizedBusinessContextFilter(routeString(route.query.finalidade))
);

const contactId = computed(() => {
  const normalized = Number(route.query.contact_id);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
});

const headerHelper = computed(() =>
  contactId.value
    ? 'Organize as listas salvas deste cliente, acompanhe os imóveis favoritados e entre rapidamente em cada coleção.'
    : 'Acesse seus imóveis favoritos e os alertas criados de acordo com seus filtros.'
);

const breadcrumb = computed(() => {
  if (!contact.value?.id) {
    return [{ label: 'Favoritos e listas' }];
  }

  return [
    { label: 'Clientes', to: { name: 'lais_leads_index' } },
    {
      label: contact.value.name || 'Cliente',
      to: { name: 'lais_lead_historico', params: { id: contact.value.id } },
    },
    { label: 'Favoritos e listas' },
  ];
});

const emptyMessage = computed(() => {
  if (contactId.value) {
    return 'Este cliente ainda não tem nenhuma lista criada. Você pode começar criando uma nova lista agora.';
  }

  return 'Ainda não há imóveis salvos. Para salvar os seus imóveis favoritos aqui, ao buscar, basta tocar no ícone do coração.';
});

const filteredLists = computed(() => {
  const query = listSearch.value.trim().toLowerCase();

  return lists.value.filter(list => {
    const searchable = [
      list.list_name,
      list.contact?.name,
      list.cover_favorite?.imovel?.titulo,
      list.cover_favorite?.imovel?.bairro,
      list.cover_favorite?.imovel?.cidade,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    const matchesQuery = !query || searchable.includes(query);
    const matchesAvailability =
      availabilityFilter.value === 'all' ||
      (availabilityFilter.value === 'available' &&
        Number(list.unavailable_count || 0) === 0) ||
      (availabilityFilter.value === 'unavailable' &&
        Number(list.unavailable_count || 0) > 0);
    const matchesBusinessContext =
      businessContextFilter.value === 'all' ||
      (businessContextFilter.value === 'aluguel' &&
        Number(list.rent_count || 0) > 0) ||
      (businessContextFilter.value === 'venda' &&
        Number(list.sale_count || 0) > 0);

    return matchesQuery && matchesAvailability && matchesBusinessContext;
  });
});

const listStats = computed(() => {
  const totalFavorites = lists.value.reduce(
    (sum, list) => sum + Number(list.total_favorites || list.items_count || 0),
    0
  );
  const unavailable = lists.value.reduce(
    (sum, list) => sum + Number(list.unavailable_count || 0),
    0
  );
  const rent = lists.value.reduce(
    (sum, list) => sum + Number(list.rent_count || 0),
    0
  );
  const sale = lists.value.reduce(
    (sum, list) => sum + Number(list.sale_count || 0),
    0
  );

  return {
    lists: lists.value.length,
    totalFavorites,
    unavailable,
    available: Math.max(0, totalFavorites - unavailable),
    rent,
    sale,
  };
});

const businessContextTabs = computed(() => [
  {
    value: 'all',
    label: 'Todos',
    count: listStats.value.totalFavorites,
  },
  {
    value: 'aluguel',
    label: 'Alugar',
    count: listStats.value.rent,
  },
  {
    value: 'venda',
    label: 'Comprar',
    count: listStats.value.sale,
  },
]);

const filteredEmptyMessage = computed(() => {
  if (businessContextFilter.value === 'aluguel') {
    return 'Nenhuma lista com imóveis para alugar foi encontrada para os filtros atuais.';
  }

  if (businessContextFilter.value === 'venda') {
    return 'Nenhuma lista com imóveis para comprar foi encontrada para os filtros atuais.';
  }

  return 'Nenhuma lista encontrada para os filtros atuais.';
});

const formatDateTime = value => {
  if (!value) return 'Sem atividade recente';
  return new Date(value).toLocaleString('pt-BR');
};

const formatCurrency = value => {
  if (value == null || value === '') return null;

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const coverPrice = coverFavorite => {
  const imovel = coverFavorite?.imovel;
  if (!imovel) return null;

  if (imovel.finalidade === 'aluguel' && imovel.valor_aluguel) {
    return formatCurrency(imovel.valor_aluguel);
  }

  if (imovel.valor_venda) {
    return formatCurrency(imovel.valor_venda);
  }

  return null;
};

const availabilityCountFor = list =>
  Math.max(
    0,
    Number(list.total_favorites || 0) - Number(list.unavailable_count || 0)
  );

const daysSince = value => {
  if (!value) return Number.POSITIVE_INFINITY;

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return Number.POSITIVE_INFINITY;

  return (Date.now() - timestamp) / MS_PER_DAY;
};

const isActiveFavoriteList = list => {
  const totalFavorites = Number(list.total_favorites || list.items_count || 0);
  if (totalFavorites <= 0) return false;

  return (
    availabilityCountFor(list) > 0 ||
    daysSince(list.last_favorited_at) <= ACTIVE_LIST_WINDOW_DAYS
  );
};

const activeLists = computed(() =>
  filteredLists.value.filter(list => isActiveFavoriteList(list))
);

const inactiveLists = computed(() =>
  filteredLists.value.filter(list => !isActiveFavoriteList(list))
);

const hasOnlyInactiveLists = computed(
  () => activeLists.value.length === 0 && inactiveLists.value.length > 0
);

const favoriteListSections = computed(() =>
  [
    {
      key: 'active',
      title: 'Listas ativas',
      description:
        'Listas com pelo menos um imóvel disponível ou atividade nos últimos 90 dias.',
      lists: activeLists.value,
      isInactive: false,
    },
    {
      key: 'inactive',
      title: 'Listas inativas',
      description:
        'Listas sem favoritos recentes, vazias ou sem imóveis disponíveis no momento.',
      lists: inactiveLists.value,
      isInactive: true,
    },
  ].filter(section => section.lists.length > 0)
);

const availableLabel = count =>
  `${count} ${count === 1 ? 'disponível' : 'disponíveis'}`;

const unavailableLabel = count =>
  `${count} ${count === 1 ? 'indisponível' : 'indisponíveis'}`;

const listCountLabel = count => `${count} ${count === 1 ? 'lista' : 'listas'}`;

const loadContact = async () => {
  if (!contactId.value) {
    contact.value = null;
    return;
  }

  contactLoading.value = true;
  try {
    const { data } = await LaisCrmContactsAPI.show(contactId.value);
    contact.value = data.payload || null;
  } catch {
    contact.value = null;
    useAlert('Não foi possível carregar o cliente selecionado.');
  } finally {
    contactLoading.value = false;
  }
};

const loadLists = async () => {
  loading.value = true;
  try {
    const { data } = await FavoriteListsAPI.list({
      contactId: contactId.value,
    });
    lists.value = data.data || [];
  } catch (error) {
    lists.value = [];
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível carregar as listas de favoritos.'
    );
  } finally {
    loading.value = false;
  }
};

const loadPage = async () => {
  await Promise.all([loadContact(), loadLists()]);
};

const openLeadHistory = () => {
  if (!contact.value?.id) return;

  router.push({
    name: 'lais_lead_historico',
    params: { accountId: accountId.value, id: contact.value.id },
  });
};

const openImoveisForLead = () => {
  if (!contactId.value) return;

  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
    query: { contact_id: String(contactId.value) },
  });
};

const openBulkAddForLead = ({ listName } = {}) => {
  if (!contactId.value) {
    useAlert(
      'Abra os favoritos a partir de um cliente para adicionar imóveis.'
    );
    return;
  }

  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
    query: compactQuery({
      contact_id: String(contactId.value),
      favorite_list: listName,
    }),
  });
};

const createList = async name => {
  if (!contactId.value) {
    useAlert(
      'Abra esta área a partir de um cliente para criar uma nova lista.'
    );
    return;
  }

  const trimmedName = name?.trim() || '';
  if (trimmedName.length < 3) {
    useAlert('O nome precisa ter ao menos 3 caracteres.');
    return;
  }

  creating.value = true;
  try {
    const { data } = await FavoriteListsAPI.create({
      contactId: contactId.value,
      name: trimmedName,
    });

    showCreateDialog.value = false;
    useAlert('Lista criada com sucesso.');
    await loadLists();
    router.push({
      name: 'favoritos_show',
      params: { accountId: accountId.value, listId: data.id },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível criar esta lista agora.'
    );
  } finally {
    creating.value = false;
  }
};

const openDeleteList = list => {
  listPendingDeletion.value = list;
  showDeleteDialog.value = true;
};

const closeDeleteDialog = () => {
  if (deleting.value) return;

  showDeleteDialog.value = false;
  listPendingDeletion.value = null;
};

const deleteList = async () => {
  const list = listPendingDeletion.value;
  if (!list?.id) return;

  const previousLists = [...lists.value];
  deleting.value = true;
  lists.value = lists.value.filter(item => item.id !== list.id);

  try {
    await FavoriteListsAPI.destroy(list.id);
    showDeleteDialog.value = false;
    listPendingDeletion.value = null;
    track('favorites_list_deleted', {
      list_id: list.id,
      total_favorites: Number(list.total_favorites || 0),
    });
    useAlert('Lista excluída com sucesso.');
  } catch (error) {
    lists.value = previousLists;
    useAlert(
      error.response?.data?.errors?.[0] ||
        'Não foi possível excluir esta lista agora.'
    );
  } finally {
    deleting.value = false;
  }
};

const querySignature = query =>
  Object.entries(query)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${Array.isArray(value) ? value[0] : value}`)
    .join('|');

const syncFavoritesRouteState = () => {
  const nextQuery = compactQuery({
    ...route.query,
    q: listSearch.value || undefined,
    disponibilidade:
      availabilityFilter.value === 'all' ? undefined : availabilityFilter.value,
    finalidade:
      businessContextFilter.value === 'all'
        ? undefined
        : businessContextFilter.value,
  });

  if (querySignature(route.query) === querySignature(nextQuery)) return;

  router.replace({ query: nextQuery }).catch(() => {});
};

watch(
  [listSearch, availabilityFilter, businessContextFilter],
  syncFavoritesRouteState
);

watch(
  () => route.query.contact_id,
  () => {
    loadPage();
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Favoritos e listas de imóveis da Imudei"
      :helper="headerHelper"
      :breadcrumb="breadcrumb"
    >
      <template #actions>
        <LaisPrimaryButton
          icon="i-lucide-folder-plus"
          :disabled="!contactId"
          @click="showCreateDialog = true"
        >
          Nova lista
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="contactId"
          icon="i-lucide-heart-plus"
          variant="ghost"
          @click="openBulkAddForLead()"
        >
          Adicionar em lote
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="contactId"
          icon="i-lucide-search"
          variant="ghost"
          @click="openImoveisForLead"
        >
          Buscar mais imóveis
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]"
      >
        <div class="grid gap-5">
          <LaisCard
            v-if="contactId"
            :title="
              contactLoading ? 'Carregando cliente...' : 'Contexto do cliente'
            "
          >
            <div class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <template v-if="contact">
                <p>
                  Você está organizando os favoritos de
                  <strong class="text-lais-text">
                    {{ contact.name || 'Cliente atual' }}.
                  </strong>
                </p>
                <p>
                  {{
                    [contact.email, contact.phone_number]
                      .filter(Boolean)
                      .join(' · ') ||
                    'Sem e-mail ou telefone principal cadastrado.'
                  }}
                </p>
              </template>
              <template v-else>
                <p>
                  Não foi possível carregar os dados completos deste cliente.
                </p>
              </template>

              <div class="flex flex-wrap gap-2">
                <RouterLink
                  v-if="contact"
                  :to="{
                    name: 'lais_lead_historico',
                    params: { accountId, id: contact.id },
                  }"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-history size-4" />
                  Voltar para o histórico
                </RouterLink>
                <button
                  v-if="contact"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                  @click="openLeadHistory"
                >
                  <span class="i-lucide-user-round size-4" />
                  Abrir cliente
                </button>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Listas salvas">
            <div v-if="loading" class="py-8 text-[13px] text-lais-text-muted">
              Carregando listas...
            </div>

            <div v-else-if="lists.length === 0" class="space-y-4">
              <p class="text-[13px] leading-6 text-lais-text-muted">
                {{ emptyMessage }}
              </p>
              <RouterLink
                v-if="!contactId"
                :to="{ name: 'lais_leads_index', params: { accountId } }"
                class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[13px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
              >
                <span class="i-lucide-users size-4" />
                Abrir clientes
              </RouterLink>
              <LaisPrimaryButton
                v-else
                icon="i-lucide-heart-plus"
                @click="openBulkAddForLead()"
              >
                Adicionar imóveis em lote
              </LaisPrimaryButton>
            </div>

            <div v-else class="space-y-4">
              <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Listas
                  </p>
                  <p class="mt-1 text-[22px] font-semibold text-lais-text">
                    {{ listStats.lists }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Favoritos
                  </p>
                  <p class="mt-1 text-[22px] font-semibold text-lais-text">
                    {{ listStats.totalFavorites }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Disponíveis
                  </p>
                  <p class="mt-1 text-[22px] font-semibold text-lais-text">
                    {{ listStats.available }}
                  </p>
                </div>
                <div
                  class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-4 py-3"
                >
                  <p
                    class="text-[11px] font-semibold uppercase text-lais-text-muted"
                  >
                    Indisponíveis
                  </p>
                  <p class="mt-1 text-[22px] font-semibold text-lais-text">
                    {{ listStats.unavailable }}
                  </p>
                </div>
              </div>

              <div
                class="flex flex-wrap items-center gap-2 rounded-lais border border-lais-border bg-white p-1"
                aria-label="Filtrar listas por finalidade"
              >
                <button
                  v-for="tab in businessContextTabs"
                  :key="tab.value"
                  type="button"
                  class="inline-flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-[6px] px-3 py-2 text-[13px] font-semibold transition-all sm:flex-none"
                  :class="
                    businessContextFilter === tab.value
                      ? 'bg-lais-primary text-white shadow-sm'
                      : 'text-lais-text-muted hover:bg-lais-bg-soft hover:text-lais-text'
                  "
                  @click="businessContextFilter = tab.value"
                >
                  <span>{{ tab.label }}</span>
                  <span
                    class="rounded-full px-2 py-0.5 text-[11px]"
                    :class="
                      businessContextFilter === tab.value
                        ? 'bg-white/20 text-white'
                        : 'bg-lais-bg-soft text-lais-text-muted'
                    "
                  >
                    {{ tab.count }}
                  </span>
                </button>
              </div>

              <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                <label
                  class="flex flex-col gap-1.5 text-[13px] font-semibold text-lais-text"
                >
                  Buscar lista
                  <input
                    v-model="listSearch"
                    type="search"
                    class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
                    placeholder="Nome, cliente, imóvel ou bairro"
                  />
                </label>
                <label
                  class="flex flex-col gap-1.5 text-[13px] font-semibold text-lais-text"
                >
                  Disponibilidade
                  <select
                    v-model="availabilityFilter"
                    class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-50"
                  >
                    <option value="all">Todas</option>
                    <option value="available">Somente disponíveis</option>
                    <option value="unavailable">Com indisponíveis</option>
                  </select>
                </label>
              </div>

              <div
                v-if="filteredLists.length === 0"
                class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-6 text-[13px] text-lais-text-muted"
              >
                {{ filteredEmptyMessage }}
              </div>

              <div v-else class="space-y-6">
                <div
                  v-if="hasOnlyInactiveLists"
                  class="rounded-lais border border-[#fedf89] bg-[#fffaeb] px-4 py-3 text-[13px] leading-6 text-[#93370d]"
                >
                  Nenhuma lista ativa no momento. As listas abaixo só têm
                  imóveis indisponíveis.
                </div>

                <section
                  v-for="section in favoriteListSections"
                  :key="section.key"
                  class="space-y-3"
                >
                  <div class="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h3 class="text-[15px] font-semibold text-lais-text">
                        {{ section.title }}
                      </h3>
                      <p class="mt-1 text-[12px] text-lais-text-muted">
                        {{ section.description }}
                      </p>
                    </div>
                    <span
                      class="rounded-full bg-lais-bg-soft px-3 py-1 text-[12px] font-semibold text-lais-text-muted"
                    >
                      {{ listCountLabel(section.lists.length) }}
                    </span>
                  </div>

                  <div class="grid gap-4 md:grid-cols-2">
                    <article
                      v-for="list in section.lists"
                      :key="list.id"
                      class="overflow-hidden rounded-lais border border-lais-border bg-white transition-all hover:-translate-y-0.5 hover:border-lais-primary hover:shadow-lais-soft"
                      :class="section.isInactive ? 'opacity-80' : ''"
                    >
                      <div class="flex min-h-[132px]">
                        <div
                          class="flex w-[112px] shrink-0 items-center justify-center bg-[#f4ecff]"
                        >
                          <img
                            v-if="list.cover_favorite?.imovel?.thumbnail"
                            :src="list.cover_favorite.imovel.thumbnail"
                            :alt="
                              list.cover_favorite.imovel.titulo ||
                              list.list_name
                            "
                            class="h-full w-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-heart size-8 text-[#d4546a]"
                            aria-hidden="true"
                          />
                        </div>

                        <div class="flex min-w-0 flex-1 flex-col px-4 py-4">
                          <div class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                              <p
                                class="truncate text-[15px] font-semibold text-lais-text"
                              >
                                {{ list.list_name }}
                              </p>
                              <p class="mt-1 text-[12px] text-lais-text-muted">
                                {{
                                  contactId
                                    ? 'Lista do cliente atual'
                                    : list.contact?.name || 'Cliente sem nome'
                                }}
                              </p>
                            </div>
                            <span
                              class="shrink-0 rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-semibold text-lais-primary"
                            >
                              {{ list.total_favorites }} favoritos
                            </span>
                          </div>

                          <div class="mt-3 flex flex-wrap gap-2">
                            <span
                              v-if="Number(list.rent_count || 0) > 0"
                              class="rounded-full bg-[#eff8ff] px-2.5 py-1 text-[11px] font-semibold text-[#175cd3]"
                            >
                              {{ list.rent_count }} para alugar
                            </span>
                            <span
                              v-if="Number(list.sale_count || 0) > 0"
                              class="rounded-full bg-[#f4f3ff] px-2.5 py-1 text-[11px] font-semibold text-[#5925dc]"
                            >
                              {{ list.sale_count }} para comprar
                            </span>
                            <span
                              class="rounded-full bg-[#ecfdf3] px-2.5 py-1 text-[11px] font-semibold text-[#027a48]"
                            >
                              {{ availableLabel(availabilityCountFor(list)) }}
                            </span>
                            <span
                              v-if="list.unavailable_count"
                              class="rounded-full bg-[#fff6ed] px-2.5 py-1 text-[11px] font-semibold text-[#c4320a]"
                            >
                              {{
                                unavailableLabel(Number(list.unavailable_count))
                              }}
                            </span>
                            <span
                              v-if="section.isInactive"
                              class="rounded-full bg-[#f2f4f7] px-2.5 py-1 text-[11px] font-semibold text-[#475467]"
                            >
                              Inativa
                            </span>
                          </div>

                          <div
                            class="mt-4 space-y-1 text-[12px] text-lais-text-muted"
                          >
                            <p
                              v-if="list.cover_favorite?.imovel?.titulo"
                              class="line-clamp-2"
                            >
                              {{ list.cover_favorite.imovel.titulo }}
                            </p>
                            <p>
                              {{
                                [
                                  list.cover_favorite?.imovel?.bairro,
                                  list.cover_favorite?.imovel?.cidade,
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || 'Sem imóvel de capa ainda'
                              }}
                            </p>
                            <p v-if="coverPrice(list.cover_favorite)">
                              {{ coverPrice(list.cover_favorite) }}
                            </p>
                          </div>

                          <p
                            class="mt-auto pt-4 text-[11px] text-lais-text-muted"
                          >
                            Última atividade:
                            {{ formatDateTime(list.last_favorited_at) }}
                          </p>

                          <div class="mt-4 flex flex-wrap gap-2">
                            <RouterLink
                              :to="{
                                name: 'favoritos_show',
                                params: { accountId, listId: list.id },
                              }"
                              class="inline-flex min-h-[36px] items-center gap-2 rounded-lais bg-lais-primary px-3 py-2 text-[12px] font-semibold text-white transition-all hover:bg-[#3c236c]"
                            >
                              <span class="i-lucide-folder-open size-4" />
                              Abrir lista
                            </RouterLink>
                            <button
                              v-if="contactId"
                              type="button"
                              class="inline-flex min-h-[36px] items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                              @click="
                                openBulkAddForLead({
                                  listName: list.list_name,
                                })
                              "
                            >
                              <span class="i-lucide-heart-plus size-4" />
                              Adicionar imóveis
                            </button>
                            <button
                              type="button"
                              class="inline-flex min-h-[36px] items-center gap-2 rounded-lais border border-[#fecdca] bg-white px-3 py-2 text-[12px] font-semibold text-[#b42318] transition-all hover:bg-[#fffbfa]"
                              @click="openDeleteList(list)"
                            >
                              <span class="i-lucide-trash-2 size-4" />
                              Excluir
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                </section>
              </div>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="Ações rápidas">
            <div class="space-y-4 text-[13px] leading-6 text-lais-text-muted">
              <p v-if="contactId">
                Separe os favoritos por região, tipo de imóvel ou momento da
                jornada do cliente.
              </p>
              <p v-else>
                Para criar uma nova lista vazia, abra esta área a partir do
                histórico de um cliente específico.
              </p>

              <div class="flex flex-wrap gap-2">
                <LaisPrimaryButton
                  icon="i-lucide-folder-plus"
                  :disabled="!contactId"
                  @click="showCreateDialog = true"
                >
                  Criar lista
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="contactId"
                  icon="i-lucide-search"
                  variant="ghost"
                  @click="openImoveisForLead"
                >
                  Continuar buscando
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="contactId"
                  icon="i-lucide-heart-plus"
                  variant="ghost"
                  @click="openBulkAddForLead()"
                >
                  Adicionar em lote
                </LaisPrimaryButton>
              </div>
            </div>
          </LaisCard>

          <LaisCard title="Resumo">
            <div class="grid gap-3 text-[13px] leading-6 text-lais-text-muted">
              <p>
                Listas salvas:
                <strong class="text-lais-text">{{ listStats.lists }}</strong>
              </p>
              <p>
                Favoritos disponíveis:
                <strong class="text-lais-text">{{
                  listStats.available
                }}</strong>
              </p>
              <p>
                Favoritos indisponíveis:
                <strong class="text-lais-text">{{
                  listStats.unavailable
                }}</strong>
              </p>
            </div>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>

  <CreateEditListDialog
    mode="create"
    :show="showCreateDialog"
    :is-loading="creating"
    :disabled="!contactId"
    @close="showCreateDialog = false"
    @submit="createList"
  />

  <DeleteListDialog
    :show="showDeleteDialog"
    :list-name="listPendingDeletion?.list_name || ''"
    :is-loading="deleting"
    @close="closeDeleteDialog"
    @confirm="deleteList"
  />
</template>
